import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Credit from './Credit'
import { useI18n } from '../i18n/LanguageContext'

const LightboxContext = createContext({ open: () => {} })

export const useLightbox = () => useContext(LightboxContext)

function Viewer({ items, index, onIndex, onClose }) {
  const { t } = useI18n()
  const closeRef = useRef(null)
  const photo = items[index]
  const count = items.length

  const go = useCallback(
    (step) => onIndex((index + step + count) % count),
    [index, count, onIndex],
  )

  useEffect(() => {
    const previous = document.activeElement
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = overflow
      previous?.focus?.()
    }
  }, [])

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
      else if (event.key === 'ArrowRight') go(1)
      else if (event.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, onClose])

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={t.common.photoViewer}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <button ref={closeRef} type="button" className="lightbox-close" onClick={onClose} aria-label={t.common.closeViewer}>
        ×
      </button>
      {count > 1 && (
        <>
          <button type="button" className="lightbox-nav lightbox-prev" onClick={() => go(-1)} aria-label={t.common.previousPhoto}>‹</button>
          <button type="button" className="lightbox-nav lightbox-next" onClick={() => go(1)} aria-label={t.common.nextPhoto}>›</button>
        </>
      )}
      <figure className="lightbox-figure">
        <img key={photo.id} src={photo.src} alt={photo.alt} />
        <figcaption>
          <span>{photo.caption}</span>
          <Credit photo={photo} />
          <span className="lightbox-count">{index + 1} / {count}</span>
        </figcaption>
      </figure>
    </div>
  )
}

export function LightboxProvider({ children }) {
  const [state, setState] = useState(null)
  const open = useCallback((items, index = 0) => setState({ items, index }), [])
  const close = useCallback(() => setState(null), [])
  const setIndex = useCallback((index) => setState((s) => (s ? { ...s, index } : s)), [])

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      {state && <Viewer items={state.items} index={state.index} onIndex={setIndex} onClose={close} />}
    </LightboxContext.Provider>
  )
}
